<script>
    import hosted from '../private/hosted';
    import $device from '../../global/device';
    import $media from '../../global/media';
    import $util from '../../global/util';
    import iconClose from '../../icon/misc/close.svg';
    import {
        minScrollbarWidth,
        minScrollbarHeight
    } from '../../init';

    const scrollTimeout = 160;    // ms
    const shadowSpread = 24;      // px
    const threshold = 64;         // px
    const smooth = 'smooth';      // smoooooth

    /**
     * Workaround: iOS <= 13 falsely reports scroll positions either at the beginning or end of the scroll range to the
     * scroll listener during drag. The workaround ignores scroll positions that are probably false.
     */
    const needScrollFix = $device.ios && /OS 1[0-3]_/.test(navigator.userAgent);


    /**
     * This is a surface (sheet) docked to a window side. By default, it is a bottom sheet for portrait orientation and
     * an end (right for LTR and left for RTL) side sheet for landscape orientation. It is draggable on touch devices,
     * and can have a minimum area still visible in the "closed" state. It is modal by default, but can be set to
     * modeless.
     *
     * It is implemented using Scroll Snap, which allows fluid movement while dragging on mobile devices.
     *
     * Headers and footers, if specified, do not scroll. A typical scenario is to put a `ystop` at the bottom of the
     * header, allowing the header to remain always visible if docked at the window bottom, and act as a non-scrolling
     * handle that can be dragged or clicked to open the rest of the drawer.
     */
    export default {
        name: 'ib-drawer',

        mixins: [
            hosted
        ],

        inheritAttrs: false,

        props: {
            /**
             * If `true`, show this drawer in the fully open position. If numeric, show this drawer opened to the stop
             * with the specified index. Use `v-model` to keep the mapped variable in sync when the user manipulates
             * the drawer position.
             */
            value: {
                type: [Boolean,Number],
                default: true
            },
            /**
             * If `true`, this is a modeless drawer, and will not block interaction with other
             * user interface elements.
             */
            modeless: Boolean,
            /**
             * Width in open position, not including handle, in any CSS units.
             * By default, will fit to content.
             */
            width: String,
            /**
             * Height in open position, not including handle, in any CSS units.
             * By default, will fit to content.
             */
            height: String,
            /**
             * Stop position, or array of stop positions, in any CSS units, for the horizontal axis. The first position
             * (or only position, if only one is specified) will be the minimum visible area of the drawer. There is
             * always an implied stop at the maximum width of the pane, you do not need to include it here.
             */
            xstop: {
                type: [String,Array],
                default: '0'
            },
            /**
             * Stop position, or array of stop positions, in any CSS units, for the vertical axis. The first position
             * (or only position, if only one is specified) will be the minimum visible area of the drawer. There is
             * always an implied stop at the maximum height of the pane, you do not need to include it here.
             */
            ystop: {
                type: [String,Array],
                default: '0'
            },
            /**
             * Manually set z-index; default z-index is 1050. For example, to overlay dialogs, use
             * 1052.
             */
            zIndex: Number,
            /**
             * Requested position relative to window [auto, top, right, bottom, left, start, end];
             * default is auto, which means anchored to either the bottom or end window edge,
             * whichever is shorter.
             */
            position: {
                type: String,
                default: 'auto'
            },
            /**
             * If `true`, display a handle. By default, this is `true` for top and bottom drawers,
             * and `false` for left and right drawers.
             */
            handle: {
                type: Boolean,
                default: undefined
            },
            /**
             * If `true`, close this panel when clicking the backdrop. This does not work with
             * modeless drawers.
             */
            autoclose: {
                type: Boolean,
                default: true
            },
            /**
             * If `true`, display a close button in the header. If `false`, do not display a close
             * button in the header. By default, a close button will be displayed if a header is
             * specified and the primary pointing device is a mouse.
             */
            closeButton: {
                type: Boolean,
                default: undefined
            }
        },

        data() {
            return {
                state: false,   // opened beyond threshold; may not always be the same as ivalue,
                dodge_: 0
            };
        },

        computed: {
            sp() {
                return $util.scrollProps(this.$rtl);
            },

            inlineStart() {
                return this.$rtl ? 'right' : 'left';
            },

            inlineEnd() {
                return this.$rtl ? 'left' : 'right';
            },

            vertical() {
                return this.actual == 'top' || this.actual == 'bottom';
            },

            actual() {
                return {
                    auto: $media.orientation == 'landscape' ? this.inlineEnd : 'bottom',
                    top: 'top',
                    left: 'left',
                    right: 'right',
                    start: this.inlineStart,
                    end: this.inlineEnd
                }[this.position] || 'bottom';
            },

            inverse() {
                return {
                    top: 'bottom',
                    left: 'right',
                    right: 'left',
                    bottom: 'top'
                }[this.actual];
            },

            fullSize() {
                const dodge = this.dodge_;
                const base = this.vertical ? '100vh' : '100vw';
                return dodge ? 'calc(' + base + ' - ' + dodge + 'px)' : base;
            },

            openDir() {
                return this.actual == 'bottom' || this.actual == 'right';
            },

            showHandle() {
                return this.handle != null ? this.handle : this.vertical;
            },

            stop() {
                const stop = this.vertical ? this.ystop : this.xstop;
                return Array.isArray(stop) ? stop : [stop];
            },

            offset() {
                return $util.topx(this.stop[0]) || 0;
            },

            outerStyle() {
                const dodge = this.dodge_;
                const vertical = this.vertical;
                return {
                    [this.actual]: dodge ? dodge + 'px' : null,
                    [vertical ? 'height' : 'width']: this.offset + 'px',
                    'zIndex': this.zIndex
                };
            },

            railStyle() {
                return this.vertical ? {
                    marginInlineEnd: -(minScrollbarWidth || 16) + 'px',
                    height: this.dodge_ ? this.fullSize : null
                } : {
                    marginBottom: -(minScrollbarHeight || 16) + 'px',
                    width: this.dodge_ ? this.fullSize : null
                };
            },

            paneStyle() {
                switch (this.actual) {
                case 'top':
                    return {
                        height: this.height,
                        marginInlineEnd: (minScrollbarWidth ? 0 : 16) + 'px',
                        marginBottom: -this.offset + 'px'
                    };
                case 'bottom':
                    return {
                        height: this.height,
                        marginInlineEnd: (minScrollbarWidth ? 0 : 16) + 'px',
                        marginTop: -this.offset + 'px'
                    };
                case 'left':
                    return {
                        width: this.width,
                        marginBottom: (minScrollbarHeight ? 0 : 16) + 'px',
                        marginRight: -this.offset + 'px'
                    };
                case 'right':
                    return {
                        width: this.width,
                        marginBottom: (minScrollbarHeight ? 0 : 16) + 'px',
                        marginLeft: -this.offset + 'px'
                    };
                }
            }
        },

        beforeCreate() {
            // not reactive
            this.backdropOpacity = 0;
            this.scrolling = false;
        },

        mounted() {
            (this.ivalue ? this.capture : this.release)('auto');
            if ($device.ios) {
                const rail = this.proxy_.$refs.rail;
                rail.addEventListener('touchmove', this.touchmove, { passive: false });
                rail.addEventListener('touchforcechange', this.touchmove, { passive: false });
            }
        },

        beforeDestroy() {
            this.clipto && clearTimeout(this.clipto);
            this.scrollto && clearTimeout(this.scrollto);
            if (this.ivalue) {
                this.$emit('input', false);
            }
        },

        render(h) {
            const vm = this;

            // Satisfy vue-docgen-api
            if (0) {
                /**
                 * @slot This slot accepts header text, or other content that appears at the top of the drawer. It is
                 * never scrolled out of view.
                 */
                const sheader = this.$slots.header;
                /**
                 * @slot This slot accepts footer buttons, or other content that appears at the bottom of the drawer. It is
                 * never scrolled out of view.
                 */
                const sfooter = this.$slots.footer;
                /**
                 * @slot Main content. Will scroll on overflow.
                 */
                const sdefault = this.$slots.default;
            }

            const sheader = vm._t('header');
            const sfooter = vm._t('footer');
            const sdefault = vm._t('default');
            const modal = !vm.modeless;
            const actual = vm.actual;
            const closeButton = vm.closeButton;

            return h('transition', {
                'attrs': {
                    'name': '_tr-drawer',
                    'appear': true,
                    'duration': 500
                }
            }, [
                h('div', {
                    'staticClass': '_drwr',
                    'class': {
                        ['_drwr-' + actual]: true,
                        '_drwr-start': actual == this.inlineStart,
                        '_drwr-end': actual == this.inlineEnd,
                        '_drwr-modal': modal,
                        '_drwr-open': vm.state,
                        '_drwr-dock': !vm.state,
                        '_drwr-clip': !vm.state && !!vm.dodge_,
                        '_drwr-scroll': !!vm.scrolling,
                        '_drwr-touch': vm.touch && !vm.$device.ios
                    },
                    'style': vm.outerStyle,
                    'attrs': {
                        'tabindex': 0
                    },
                    'on': {
                        keydown: this.keydown
                    }
                }, [
                    modal ? h('div', {
                        'ref': 'backdrop',
                        'staticClass': '_drwr-backdrop',
                        'style': {
                            opacity: vm.backdropOpacity
                        }
                    }) : void 0,
                    h('div', {
                        'ref': 'rail',
                        'staticClass': '_drwr-rail',
                        'style': vm.railStyle,
                        'on': {
                            scroll: vm.scroll,
                            mousedown: vm.mousedown,
                            touchstart: vm.touchstart,
                            touchend: vm.touchend
                        }
                    }, [
                        h('div', {
                            'ref': 'pane',
                            'staticClass': vm.classAttrs('_drwr-pane'),
                            'class': {
                                '_drwr-handle': vm.showHandle
                            },
                            'attrs': {
                                'role': 'dialog',
                                'aria-modal': modal,
                                ...vm.$attrs
                            },
                            'style': vm.paneStyle,
                            'on': vm.state ? null : {
                                click: vm.show
                            },
                            'directives': actual == 'bottom' ? [{
                                name: 'size',
                                rawName: 'v-size',
                                value: vm.bottomAlign,
                                expression: 'size',
                                modifiers: {
                                    height: true
                                }
                            }] : void 0
                        }, [
                            sheader || closeButton ? h('div', {
                                'staticClass': '_drwrh border-bottom'
                            }, [
                                (closeButton === void 0 ? $device.mouse : closeButton) ? h('ib-button', {
                                    'staticClass': '_drwr-btn clear pill fs7',
                                    'domProps': {
                                        'innerHTML': iconClose
                                    },
                                    'on': {
                                        'click': vm.hide
                                    }
                                }) : vm._e(),
                                ...(sheader || [])
                            ]) : vm._e(),
                            h('div', {
                                'staticClass': '_drwri'
                            }, [
                                sdefault
                            ]),
                            sfooter ? h('div', {
                                'staticClass': '_drwrf border-top'
                            }, sfooter) : vm._e()
                        ]),
                        vm.stop.map(stop => h('div', {
                            'staticClass': '_drwr-stop',
                            'style': {
                                [vm.inverse]: $util.topx(stop) - vm.offset + 'px'
                            }
                        }))
                    ])
                ])
            ]);
        },

        methods: {
            capture(behavior = smooth) {
                if (behavior != smooth || this.scrollDir !== this.openDir) {
                    // Workaround: Firefox doesn't initiate smooth scroll if the rail is fully clipped
                    if (behavior == smooth) {
                        const el = this.proxy_.$el;
                        if (this.vertical) {
                            if (!el.offsetHeight) {
                                el.style.height = '1px';
                            }
                        } else {
                            if (!el.offsetWidth) {
                                el.style.width = '1px';
                            }
                        }
                        this.scrollStart();
                    }
                    const rail = this.proxy_.$refs.rail;
                    const actual = this.actual;
                    const stop = this.stop[this.ivalue]
                      ? $util.topx(this.stop[this.ivalue]) - this.offset
                      : actual == 'bottom' ? rail.scrollHeight - rail.clientHeight
                      : actual == this.inlineEnd ? (rail.scrollWidth - rail.clientWidth) * this.sp.step
                      : 0;
                    rail.scrollTo({
                        [this.vertical ? 'top' : 'left']: stop,
                        behavior
                    });
                }
            },

            release(behavior = smooth) {
                if (behavior != smooth || this.scrollDir !== !this.openDir) {
                    const proxy = this.proxy_;
                    if (proxy) {
                        const rail = proxy.$refs.rail;
                        const actual = this.actual;
                        rail.scrollTo(
                            actual == 'bottom' ? { behavior, top: 0 } :
                            actual == 'top' ? { behavior, top: rail.scrollHeight - rail.clientHeight } :
                            actual == this.inlineEnd ? { behavior, left: 0 } :
                            actual == this.inlineStart ? { behavior, left: rail.scrollWidth - rail.clientWidth } : {});
                    }
                }
            },

            set(value) {
                if (value) {
                    this.capture();
                } else {
                    const proxy = this.proxy_;
                    if (proxy) {
                        const activeElement = document.activeElement;
                        if (activeElement && proxy.$el.contains(activeElement)) {
                            activeElement.blur();
                        }
                        this.release();
                    }
                }
                this.$emit('input', this.ivalue = value);
            },

            show() {
                this.set(true);
            },

            hide() {
                this.set(false);
            },

            scrollStart() {
                this.scrolling = true;
                this.setClip(true, 0);
            },

            scrollStop() {
                this.scrolling = false;
                this.scrollDir = null;
                this.scrollto = 0;
                this.setClip(false, 0);
            },

            scroll(e) {
                const vertical = this.vertical;
                const proxy = this.proxy_;
                if (proxy) {
                    const rail = proxy.$refs.rail;
                    const scrollPos = vertical
                        ? rail.scrollTop
                        : rail.scrollLeft * this.sp.step;
                    const scrollMax = vertical
                        ? rail.scrollHeight - rail.clientHeight
                        : rail.scrollWidth - rail.clientWidth;
                    // scrollfix is a false rail scroll position reported by iOS <= 13 that we should ignore
                    if (this.scrollfix !== void 0 && (scrollPos === this.scrollfix || scrollPos === 0)) {
                        return;
                    }
                    if (!this.modeless) {
                        const backdrop = proxy.$refs.backdrop;
                        if (backdrop) {
                            this.backdropOpacity =
                            backdrop.style.opacity =
                                Math.min(1, Math.max(0, scrollPos / scrollMax));
                        }
                    }
                    if (!this.scrolling) {
                        this.scrollStart();
                    }
                    if (this.scrollto) {
                        clearTimeout(this.scrollto);
                        this.scrollto = 0;
                    }
                    this.scrollDir = scrollPos > this.scrollPos;
                    this.scrollPos = scrollPos;
                    this.scrollLast = e.timeStamp;
                    this.scrollto = setTimeout(this.scrollStop, scrollTimeout);
                    const state = scrollPos > threshold;
                    if (this.state != state) {
                        this.state = state;
                        if (this.ivalue != state) {
                            this.set(state);
                        }
                        // iOS can handle terminating clip early to allow interaction with underlying UI; this causes flicker on Android
                        if ($device.ios) {
                            this.setClip(state, state ? 0 : threshold);
                        }
                    }
                }
            },

            setClip(scrolling, threshold) {
                if (this.clipto) {
                    clearTimeout(this.clipto);
                    this.clipto = 0;
                }
                const el = this.proxy_.$el;
                const rail = this.proxy_.$refs.rail;
                const scrollPos = Math.max(0, this.vertical ? rail.scrollTop : rail.scrollLeft * this.sp.step);
                this.proxy_.$el.classList[scrolling ? 'add' : 'remove']('_drwr-scroll');
                el.style[this.vertical ? 'height' : 'width'] = (scrolling || (!this.modeless && scrollPos > threshold))
                    ? this.fullSize
                    : this.offset + scrollPos + 'px';
            },

            mousedown(e) {
                if (e.which == 1) {
                    const proxy = this.proxy_;
                    if (proxy && !proxy.$refs.pane.contains(e.target)) {
                        if (this.autoclose && !this.modeless) {
                            this.set(false);
                            e.preventDefault();
                        }
                    }
                }
            },

            touchstart(e) {
                const proxy = this.proxy_;
                if (proxy) {
                    if (!proxy.$refs.pane.contains(e.target)) {
                        if (this.autoclose && !this.modeless) {
                            this.set(false);
                        }
                        e.preventDefault();
                    } else {
                        this.touch = true;
                        if (!this.scrolling) {
                            this.setClip(true, 0);
                        }
                        if ($device.ios) {
                            const rail = proxy.$refs.rail;
                            if (needScrollFix) {
                                this.scrollfix = this.vertical ? rail.scrollTop : rail.scrollLeft * this.sp.step;
                            }
                            if (this.scrolling && this.actual == 'bottom') {
                                // Attempt to prevent swipe gesture from moving rail when user intends to scroll pane content
                                // Hopefully in the future this can be accomplished gracefully
                                if (rail.scrollTop > threshold && rail.scrollTop > rail.scrollHeight - rail.clientHeight - threshold) {
                                    this.touchY = e.pageY;
                                }
                            }
                        } else {
                            // Changing overflow class breaks scroll on iOS so we only do this for non iOS devices
                            this.proxy_.$el.classList.add('_drwr-touch');
                        }
                    }
                }
            },

            touchend(e) {
                this.touchY = null;
                this.touch = false;
                const proxy = this.proxy_;
                if (proxy) {
                    // clear clip state set by touchstart, unless this is a click that instigates scrolling
                    this.clipto && clearTimeout(this.clipto);
                    this.clipto = setTimeout(() => {
                        if (!this.scrolling) {
                            this.setClip(false, 0);
                        }
                    }, 100);
                    if (!$device.ios) {
                        this.scrollfix = void 0;
                        this.proxy_.$el.classList.remove('_drwr-touch');
                    }
                }
            },

            touchmove(e) {
                if (this.touchY != null) {
                    if (this.touchY > e.pageY) {
                        e.preventDefault();
                        const el = $util.scrollChild(this.proxy_.$refs.pane);
                        if (el) {
                            el.scrollTop += this.touchY - e.pageY;
                        }
                    }
                    this.touchY = e.pageY;
                }
            },

            keydown(e) {
                if (e.which == 27) {
                    this.set(false);
                    e.preventDefault();
                }
            },

            // maintain bottom alignment on content resize
            bottomAlign() {
                if (this.actual == 'bottom' && this.ivalue) {
                    this.capture(smooth);
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._drwr {
        position: fixed;
        z-index: @z-drawer;
        pointer-events: none;
        overflow: hidden;
        .elevation(12, 0, .75);

        &-top {
            top: 0;
            right: 0;
            left: 0;
            &._drwr-dock {
                border-radius: 0 0 @drawer-border-radius @drawer-border-radius;
            }
            &._drwr-clip {
                clip: rect(auto auto -24px auto);
            }
            ._drwr-rail {
                top: 0;
            }
            ._drwr-pane {
                bottom: 100%;
                left: 0;
                right: 0;
                max-height: calc(~'100% - 32px');
                border-radius: 0 0 @drawer-border-radius @drawer-border-radius;
                scroll-snap-align: start;
            }
        }

        &-bottom {
            bottom: 0;
            right: 0;
            left: 0;
            &._drwr-dock {
                border-radius: @drawer-border-radius @drawer-border-radius 0 0;
            }
            &._drwr-clip {
                clip: rect(-24px auto auto auto);
            }
            ._drwr-rail {
                bottom: 0;
            }
            ._drwr-pane {
                top: 100%;
                left: 0;
                right: 0;
                max-height: calc(~'100% - 32px');
                border-radius: @drawer-border-radius @drawer-border-radius 0 0;
                scroll-snap-align: end;
            }
        }

        &-left {
            top: 0;
            bottom: 0;
            left: 0;
            &._drwr-dock {
                border-radius: 0 @drawer-border-radius @drawer-border-radius 0;
            }
            &._drwr-clip {
                clip: rect(auto -24px auto auto);
            }
            ._drwr-rail {
                left: 0;
            }
            ._drwr-pane {
                right: 100%;
                top: 0;
                bottom: 0;
                max-width: calc(~'100% - 32px');
                border-radius: 0 @drawer-border-radius @drawer-border-radius 0;
            }
        }

        &-right {
            top: 0;
            bottom: 0;
            right: 0;
            &._drwr-dock {
                border-radius: @drawer-border-radius 0 0 @drawer-border-radius;
            }
            &._drwr-clip {
                clip: rect(auto auto auto -24px);
            }
            ._drwr-rail {
                right: 0;
            }
            ._drwr-pane {
                left: 100%;
                top: 0;
                bottom: 0;
                max-width: calc(~'100% - 32px');
                border-radius: @drawer-border-radius 0 0 @drawer-border-radius;
            }
        }

        &-start {
            ._drwr-pane {
                scroll-snap-align: start;
            }
        }

        &-end {
            ._drwr-pane {
                scroll-snap-align: end;
            }
        }

        &-top ._drwr-rail,
        &-bottom ._drwr-rail {
            left: 0;
            right: 0;
            height: 100vh;
            overflow-x: hidden;
            overflow-y: scroll;
            -webkit-scroll-snap-points-y: repeat(100%);
            scroll-snap-points-y: repeat(100%);
            scroll-snap-type: y mandatory;
        }
        &-top ._drwr-handle::after,
        &-bottom ._drwr-handle::before {
            content: '';
            display: block;
            flex: 0 0 auto;
            width: 38px;
            height: 5px;
            margin: 6px auto;
            background-color: mix(@fg, @bg-dialog, 15%);
            border-radius: 5px;
        }

        &-left ._drwr-rail,
        &-right ._drwr-rail {
            top: 0;
            bottom: 0;
            width: 100vw;
            overflow-x: scroll;
            overflow-y: hidden;
            -webkit-scroll-snap-points-x: repeat(100%);
            scroll-snap-points-x: repeat(100%);
            scroll-snap-type: x mandatory;
        }

        &-open._drwr-modal,
        &-scroll {
            border-radius: 0;
            box-shadow: none;
            ._drwr-pane {
                .elevation(12, 0, .75);
            }
        }
        &-touch {
            overflow: visible;
        }

        &-backdrop {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background: @bg-backdrop;
            contain: strict;
            pointer-events: all;
        }
        &-rail {
            position: absolute;
            pointer-events: all;
            -webkit-scroll-snap-destination: 0% 0%;
            -webkit-overflow-scrolling: touch;
            -webkit-scroll-snap-type: mandatory;
            -ms-scroll-snap-type: mandatory;
            .hide-scrollbar();
        }
        &-pane {
            position: absolute;
            display: flex;
            flex-direction: column;
            background: @bg-dialog;
            overflow: hidden;
        }
        &-stop {
            position: absolute;
            width: 1px;
            height: 1px;
            scroll-snap-align: start;
        }
        &i {
            display: flex;
            flex: 1 1 auto;
            align-items: stretch;
            position: relative;
            overflow-y: auto;
        }
        &h,
        &f {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: center;
            flex: 0 0 auto;
        }
        &-btn {
            width: 36px;
            height: 36px;
            margin: 10px !important;
            padding: 0 !important;
            .bg-gray(10) !important;
            + .ib-col {
                flex-basis: calc(~'100% - 104px');
            }
        }
    }

    ._tr-drawer {
        &-enter,
        &-leave-to {
            ._drwr-backdrop {
                opacity: 0 !important;
            }
            &._drwr-top ._drwr-rail {
                transform: translateY(-100%);
            }
            &._drwr-bottom ._drwr-rail {
                transform: translateY(100%);
            }
            &._drwr-left ._drwr-rail {
                transform: translateX(-100%);
            }
            &._drwr-right ._drwr-rail {
                transform: translateX(100%);
            }
        }
        &-enter-active,
        &-leave-active {
            border-radius: 0 !important;
            box-shadow: none !important;
            ._drwr-backdrop {
                transition: opacity .5s;
            }
            ._drwr-pane {
                .elevation(12, 0, .75);
            }
        }
        &-enter-active {
            ._drwr-rail {
                transition: transform .5s ease-out;
            }
        }
        &-leave-active {
            ._drwr-rail {
                transition: transform .5s ease-in;
            }
        }
    }
</style>

<ib-example id="multi" name="Multiple Stops">
    <template lang="pug">
        div
            ib-toggle(id="show", v-model="show") Show Bottom Drawer
            ib-drawer(v-if="show", v-model="stop", position="bottom", :ystop="['80px','180px']")
                .ib-col.inset-16.text-center
                    h3.after-64
                        a(role="button", @click.prevent="stop = 1") Click Me
                    h3.after-64
                        a(role="button", @click.prevent="stop = 2") Click Me Too
                    p(v-for="i in 8") Some text.
    </template>
    <script>
        {
            data() {
                return {
                    show: false,
                    stop: 0
                };
            }
        }
    </script>
</ib-example>
